import React, { useState, useEffect } from 'react';
import { ArrowLeft, Users, MessageSquare, Activity, Send, Clock, RefreshCcw, Link, UserPlus } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import FeatureManagement from './FeatureManagement';
import InviteUserModal from './InviteUserModal';

const OrganizationDashboard = ({ organization, onBack }) => {
    // State Management
    const [activeTab, setActiveTab] = useState('overview');
    const [users, setUsers] = useState([]);
    const [features, setFeatures] = useState([]);
    const [responses, setResponses] = useState([]);
    const [adminComments, setAdminComments] = useState({});
    const [adminNotes, setAdminNotes] = useState({});
    const [recentActivity, setRecentActivity] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedFeatures, setExpandedFeatures] = useState({});
    const [success, setSuccess] = useState(null);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [metrics, setMetrics] = useState({
        totalUsers: 0,
        activeUsers: 0,
        totalResponses: 0,
        totalComments: 0,
        completionRate: 0
    });

    // Navigation tabs
    const tabs = [
        { id: 'overview', label: 'Overview' },
        { id: 'users', label: 'Users' },
        { id: 'features', label: 'Features' },
        { id: 'activity', label: 'Activity' }
    ];

    // Add these handler functions inside the OrganizationDashboard component
    const handleAddComment = async (featureRef, commentText) => {
        try {
            const response = await fetch('/api/admin/comments', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    feature_reference: featureRef,
                    organization_id: organization.id,
                    comment_text: commentText
                })
            });

            if (!response.ok) throw new Error('Failed to add comment');

            const comment = await response.json();
            setAdminComments(prev => ({
                ...prev,
                [featureRef]: [...(prev[featureRef] || []), comment]
            }));
            setSuccess('Comment added successfully');

            // Refresh comments
            const updatedComments = await fetch(`/api/admin/${organization.id}/${featureRef}/comments`, {
                credentials: 'include'
            }).then(res => res.json());
            
            setAdminComments(prev => ({
                ...prev,
                [featureRef]: updatedComments
            }));

        } catch (error) {
            console.error('Error adding comment:', error);
            setError('Failed to add comment');
        }
    };

    const handleAddNote = async (featureRef, noteText) => {
        try {
            const response = await fetch('/api/admin/notes', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    feature_reference: featureRef,
                    organization_id: organization.id,
                    note_text: noteText
                })
            });

            if (!response.ok) throw new Error('Failed to add note');

            const note = await response.json();
            setAdminNotes(prev => ({
                ...prev,
                [featureRef]: [...(prev[featureRef] || []), note]
            }));
            setSuccess('Note added successfully');

            // Refresh notes
            const updatedNotes = await fetch(`/api/admin/${organization.id}/${featureRef}/notes`, {
                credentials: 'include'
            }).then(res => res.json());
            
            setAdminNotes(prev => ({
                ...prev,
                [featureRef]: updatedNotes
            }));

        } catch (error) {
            console.error('Error adding note:', error);
            setError('Failed to add note');
        }
    };

   // In OrganizationDashboard.jsx - Replace the existing handleDeleteComment function
const handleDeleteComment = async (commentId) => {
    try {
        const response = await fetch(`/api/admin/comments/${commentId}`, {
            method: 'DELETE',
            credentials: 'include'
        });

        if (!response.ok) throw new Error('Failed to delete comment');

        setAdminComments(prev => {
            const newComments = { ...prev };
            Object.keys(newComments).forEach(featureRef => {
                if (Array.isArray(newComments[featureRef])) {
                    newComments[featureRef] = newComments[featureRef].filter(
                        comment => comment.id !== commentId
                    );
                }
            });
            return newComments;
        });
        setSuccess('Comment deleted successfully');
    } catch (error) {
        console.error('Error deleting comment:', error);
        setError('Failed to delete comment');
    }
};


const handleDeleteNote = async (noteId) => {
    try {
        const response = await fetch(`/api/admin/notes/${noteId}`, {
            method: 'DELETE',
            credentials: 'include'
        });

        if (!response.ok) throw new Error('Failed to delete note');

        setAdminNotes(prev => {
            const newNotes = { ...prev };
            Object.keys(newNotes).forEach(featureRef => {
                if (Array.isArray(newNotes[featureRef])) {
                    newNotes[featureRef] = newNotes[featureRef].filter(
                        note => note.id !== noteId
                    );
                }
            });
            return newNotes;
        });
        setSuccess('Note deleted successfully');
    } catch (error) {
        console.error('Error deleting note:', error);
        setError('Failed to delete note');
    }
};

    const handleDeleteUser = async (userId) => {
        if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            return;
        }

        try {
            const response = await fetch(`/api/admin/users/${userId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to delete user');
            }

            // Remove user from the local state
            setUsers(prev => prev.filter(user => user.id !== userId));
            setSuccess('User deleted successfully');
        } catch (error) {
            console.error('Error deleting user:', error);
            setError(error.message || 'Failed to delete user');
        }
    };

    const handleRemoveFromOrganization = async (userId) => {
        if (!confirm('Are you sure you want to remove this user from the organization?')) {
            return;
        }

        try {
            const response = await fetch(`/api/admin/organizations/${organization.id}/users/${userId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to remove user from organization');
            }

            // Remove user from the local state
            setUsers(prev => prev.filter(user => user.id !== userId));
            setSuccess('User removed from organization successfully');
        } catch (error) {
            console.error('Error removing user:', error);
            setError(error.message || 'Failed to remove user from organization');
        }
    };

    // Fetch organization data when component mounts
    useEffect(() => {
        if (!organization || !organization.id) return;
        
        const fetchOrgData = async () => {
            setLoading(true);
            setError(null);
            try {
                // First fetch that doesn't require admin routes
                const [featuresRes, responsesRes] = await Promise.all([
                    fetch(`/api/features/${organization.id}`, { 
                        credentials: 'include' 
                    }),
                    fetch(`/api/responses/${organization.id}`, { 
                        credentials: 'include' 
                    })
                ]);
        
                // Validate responses
                if (!featuresRes.ok || !responsesRes.ok) {
                    throw new Error('Failed to fetch basic organization data');
                }
        
                // Parse basic data
                const [featuresData, responsesData] = await Promise.all([
                    featuresRes.json(),
                    responsesRes.json()
                ]);
        
                // Set basic data first
                setFeatures(featuresData);
                setResponses(responsesData);
        
                // Then fetch admin-specific data
                const [
                    usersRes,
                    commentsRes,
                    notesRes,
                    activityRes,
                    metricsRes
                ] = await Promise.all([
                    fetch(`/api/admin/organizations/${organization.id}/users`, { 
                        credentials: 'include',
                        headers: {
                            'Accept': 'application/json'
                        }
                    }),
                    fetch(`/api/admin/comments/${organization.id}/comments`, { 
                        credentials: 'include',
                        headers: {
                            'Accept': 'application/json'
                        }
                    }),
                    fetch(`/api/admin/comments/${organization.id}/notes`, { 
                        credentials: 'include',
                        headers: {
                            'Accept': 'application/json'
                        }
                    }),
                    fetch(`/api/admin/organizations/${organization.id}/activity`, { 
                        credentials: 'include',
                        headers: {
                            'Accept': 'application/json'
                        }
                    }),
                    fetch(`/api/admin/organizations/${organization.id}/metrics`, { 
                        credentials: 'include',
                        headers: {
                            'Accept': 'application/json'
                        }
                    })
                ]);
        
                // Check each admin response individually to provide better error messages
                if (!usersRes.ok) {
                    console.error('Failed to fetch users:', await usersRes.text());
                    throw new Error('Failed to fetch users data');
                }
                if (!commentsRes.ok) {
                    console.error('Failed to fetch comments:', await commentsRes.text());
                    throw new Error('Failed to fetch comments data');
                }
                if (!notesRes.ok) {
                    console.error('Failed to fetch notes:', await notesRes.text());
                    throw new Error('Failed to fetch notes data');
                }
                if (!activityRes.ok) {
                    console.error('Failed to fetch activity:', await activityRes.text());
                    throw new Error('Failed to fetch activity data');
                }
                if (!metricsRes.ok) {
                    console.error('Failed to fetch metrics:', await metricsRes.text());
                    throw new Error('Failed to fetch metrics data');
                }
        
                // Parse admin data
                const [
                    usersData,
                    commentsData,
                    notesData,
                    activityData,
                    metricsData
                ] = await Promise.all([
                    usersRes.json(),
                    commentsRes.json(),
                    notesRes.json(),
                    activityRes.json(),
                    metricsRes.json()
                ]);
        
                // Validate data structures
                if (!Array.isArray(commentsData)) {
                    console.error('Invalid comments data:', commentsData);
                    throw new Error('Invalid comments data format');
                }
        
                if (!Array.isArray(notesData)) {
                    console.error('Invalid notes data:', notesData);
                    throw new Error('Invalid notes data format');
                }
        
                // Group comments and notes by feature reference
                const groupedComments = {};
                const groupedNotes = {};
        
                commentsData.forEach(comment => {
                    if (!groupedComments[comment.feature_reference]) {
                        groupedComments[comment.feature_reference] = [];
                    }
                    groupedComments[comment.feature_reference].push(comment);
                });
        
                notesData.forEach(note => {
                    if (!groupedNotes[note.feature_reference]) {
                        groupedNotes[note.feature_reference] = [];
                    }
                    groupedNotes[note.feature_reference].push(note);
                });
        
                // Update all state
                setUsers(usersData);
                setAdminComments(groupedComments);
                setAdminNotes(groupedNotes);
                setRecentActivity(activityData);
                setMetrics(metricsData || {
                    totalUsers: 0,
                    activeUsers: 0,
                    totalResponses: 0,
                    totalComments: 0,
                    completionRate: 0
                });
        
                setError(null);
            } catch (error) {
                console.error('Error fetching organization data:', error);
                setError(error.message || 'Failed to load organization data');
            } finally {
                setLoading(false);
            }
        };

        fetchOrgData();
    }, [organization?.id]);

    // User Management Functions
    const handleResetPassword = async (userId) => {
        try {
            const response = await fetch(`/api/admin/users/${userId}/reset-password`, {
                method: 'POST',
                credentials: 'include'
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to reset password');
            }

            const { message } = await response.json();
            setSuccess('Password reset email sent successfully');
        } catch (error) {
            console.error('Error resetting password:', error);
            setError(error.message.includes('Failed to send password reset email') ? 
                'Failed to send password reset email. Please try again later.' : 
                error.message);
        }
    };

    const handleUserStatusUpdate = async (userId, action) => {
        try {
            const response = await fetch(`/api/admin/users/${userId}/${action}`, {
                method: 'POST',
                credentials: 'include'
            });

            if (!response.ok) throw new Error(`Failed to ${action} user`);

            setUsers(prev => prev.map(user => 
                user.id === userId 
                    ? { ...user, status: action === 'suspend' ? 'suspended' : 'active' }
                    : user
            ));

            setSuccess(`User ${action}ed successfully`);
        } catch (error) {
            console.error(`Error ${action}ing user:`, error);
            setError(`Failed to ${action} user`);
        }
    };

    const handleCopyLoginLink = async (userId) => {
        try {
            const response = await fetch(`/api/admin/users/${userId}/login-link`, {
                method: 'POST',
                credentials: 'include'
            });

            if (!response.ok) throw new Error('Failed to generate login link');

            const { loginToken } = await response.json();
            const loginUrl = `${window.location.origin}/auto-login/${loginToken}`;
            
            await navigator.clipboard.writeText(loginUrl);
            setSuccess('Login link copied to clipboard!');
        } catch (error) {
            console.error('Error generating login link:', error);
            setError('Failed to generate login link');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg">Loading organization data...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-8">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <button 
                    onClick={onBack}
                    className="p-2 hover:bg-gray-100 rounded-full"
                >
                    <ArrowLeft className="w-6 h-6" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">{organization.name}</h1>
                    <p className="text-sm text-gray-600">
                        {organization.website_url && (
                            <a 
                                href={organization.website_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                            >
                                <Link className="w-4 h-4" />
                                {organization.website_url}
                            </a>
                        )}
                    </p>
                </div>
            </div>

            {/* Error/Success Messages */}
            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
                    {error}
                </div>
            )}
            {success && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-600">
                    {success}
                </div>
            )}

            {/* Navigation Tabs */}
            <div className="mb-6 border-b border-gray-200">
                <nav className="flex gap-4">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-4 py-2 -mb-px text-sm font-medium ${
                                activeTab === tab.id
                                    ? 'text-green-600 border-b-2 border-green-600'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
                <div className="space-y-6">
                    {/* Metrics Overview */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <Card>
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between">
                                    <Users className="w-8 h-8 text-blue-600" />
                                    <div className="text-right">
                                        <p className="text-2xl font-bold">{metrics.totalUsers}</p>
                                        <p className="text-sm text-gray-600">Total Users</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between">
                                    <Activity className="w-8 h-8 text-green-600" />
                                    <div className="text-right">
                                        <p className="text-2xl font-bold">{metrics.completionRate}%</p>
                                        <p className="text-sm text-gray-600">Features Completed</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between">
                                    <MessageSquare className="w-8 h-8 text-purple-600" />
                                    <div className="text-right">
                                        <p className="text-2xl font-bold">{metrics.totalComments}</p>
                                        <p className="text-sm text-gray-600">Comments</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between">
                                    <Clock className="w-8 h-8 text-yellow-600" />
                                    <div className="text-right">
                                        <p className="text-2xl font-bold">{metrics.activeUsers}</p>
                                        <p className="text-sm text-gray-600">Active Today</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Recent Activity and User Lists */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Recent Users */}
                        <Card className="lg:col-span-2">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle>Recent Users</CardTitle>
                                <button
                                    onClick={() => setActiveTab('users')}
                                    className="text-sm text-green-600 hover:text-green-800"
                                >
                                    View All
                                </button>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {users.slice(0, 5).map(user => (
                                        <div
                                            key={user.id}
                                            className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                                        >
                                            <div>
                                                <p className="font-medium">{user.name}</p>
                                                <p className="text-sm text-gray-600">{user.email}</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleCopyLoginLink(user.id)}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-full"
                                                    title="Copy login link"
                                                >
                                                    <Link className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleResetPassword(user.id)}
                                                    className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-full"
                                                    title="Reset password"
                                                >
                                                    <RefreshCcw className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Recent Activity */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Recent Activity</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {recentActivity.map(activity => (
                                        <div
                                            key={activity.id}
                                            className="flex items-start gap-3 pb-4 border-b last:border-0"
                                        >
                                            <Activity className="w-4 h-4 text-gray-400 mt-1" />
                                            <div>
                                                <p className="text-sm text-gray-900">
                                                    {activity.description}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {new Date(activity.timestamp).toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            )}

{activeTab === 'features' && (
                <FeatureManagement
                    selectedOrg={organization}
                    features={features}
                    adminComments={adminComments}
                    adminNotes={adminNotes}
                    handleAddComment={handleAddComment}
                    handleAddNote={handleAddNote}
                    handleDeleteComment={handleDeleteComment}
                    handleDeleteNote={handleDeleteNote}
                    expandedFeatures={expandedFeatures}
                    setExpandedFeatures={setExpandedFeatures}
                />
            )}

            {activeTab === 'users' && (
                <div className="space-y-6">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-semibold">Users</h2>
                        <button
                            onClick={() => setShowInviteModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                            <UserPlus className="w-4 h-4" />
                            Invite User
                        </button>
                        {showInviteModal && (
    <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        organization={organization}
        onSuccess={() => {
            setShowInviteModal(false);
            // Optionally show a success message
            setSuccess('Invitation sent successfully');
        }}
    />
)}
                    </div>

                    <Card>
                        <CardContent className="p-0">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            User
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Last Active
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Role
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {users.map(user => (
                                        <tr key={user.id}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {user.name}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {user.email}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                                                    ${user.status === 'active' 
                                                        ? 'bg-green-100 text-green-800'
                                                        : user.status === 'suspended'
                                                        ? 'bg-red-100 text-red-800'
                                                        : 'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                    {user.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {user.last_active 
                                                    ? new Date(user.last_active).toLocaleString()
                                                    : 'Never'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {user.role}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex space-x-3">
                                                    <button
                                                        onClick={() => handleCopyLoginLink(user.id)}
                                                        className="text-blue-600 hover:text-blue-900"
                                                        title="Copy login link"
                                                    >
                                                        <Link className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleResetPassword(user.id)}
                                                        className="text-yellow-600 hover:text-yellow-900"
                                                        title="Reset password"
                                                    >
                                                        <RefreshCcw className="w-4 h-4" />
                                                    </button>
                                                    {user.status === 'active' ? (
                                                        <button
                                                            onClick={() => handleUserStatusUpdate(user.id, 'suspend')}
                                                            className="text-red-600 hover:text-red-900"
                                                            title="Suspend user"
                                                        >
                                                            Ban
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleUserStatusUpdate(user.id, 'activate')}
                                                            className="text-green-600 hover:text-green-900"
                                                            title="Activate user"
                                                        >
                                                            Activate
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleRemoveFromOrganization(user.id)}
                                                        className="text-red-600 hover:text-red-900"
                                                        title="Remove from organization"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </CardContent>
                    </Card>
                </div>
            )}

            {activeTab === 'features' && (
                <FeatureManagement
                    selectedOrg={organization}
                    features={features}
                    adminComments={adminComments}
                    adminNotes={adminNotes}
                    handleAddComment={handleAddComment}
                    handleAddNote={handleAddNote}
                    handleDeleteComment={handleDeleteComment}
                    handleDeleteNote={handleDeleteNote}
                />
            )}

            {activeTab === 'activity' && (
                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Activity Log</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-6">
                                {recentActivity.map((activity, index) => (
                                    <div
                                        key={activity.id}
                                        className={`flex items-start gap-4 ${
                                            index !== recentActivity.length - 1 ? 'pb-6 border-b' : ''
                                        }`}
                                    >
                                        <div className="mt-1">
                                            <Activity className="w-5 h-5 text-gray-400" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm text-gray-900">
                                                {activity.description}
                                            </p>
                                            <div className="mt-1 flex items-center gap-4">
                                                <p className="text-xs text-gray-500">
                                                    {new Date(activity.timestamp).toLocaleString()}
                                                </p>
                                                {activity.user && (
                                                    <p className="text-xs text-gray-500">
                                                        by {activity.user.name}
                                                    </p>
                                                )}
                                                {activity.feature && (
                                                    <button
                                                        onClick={() => {
                                                            setActiveTab('features');
                                                            // TODO: Add logic to scroll to specific feature
                                                            const element = document.getElementById(`feature-${activity.feature.reference}`);
                                                            if (element) {
                                                                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                                element.classList.add('bg-yellow-50');
                                                                setTimeout(() => {
                                                                    element.classList.remove('bg-yellow-50');
                                                                }, 2000);
                                                            }
                                                        }}
                                                        className="text-xs text-blue-600 hover:text-blue-800"
                                                    >
                                                        View Feature
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default OrganizationDashboard;